/**
 * PaymentVerifyPage.jsx — /payment/verify
 *
 * FIX: After successful verification, calls refreshPaymentStatus()
 * from AuthContext so the payment gate in ProtectedRoute is updated
 * before redirecting to /student/choose-cohort.
 * This prevents the student from being bounced back to /payment/pending.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/apiClient';

export default function PaymentVerifyPage() {
  const navigate               = useNavigate();
  const [params]               = useSearchParams();
  const { refreshPaymentStatus } = useAuth();

  const reference = params.get('reference') || params.get('trxref');

  const [status, setStatus]   = useState('loading'); // loading | success | failed
  const [result, setResult]   = useState(null);
  const [countdown, setCount] = useState(5);

  useEffect(() => {
    if (!reference) {
      setStatus('failed');
      setResult({ message: 'No payment reference found. Please try again.' });
      return;
    }

    API.get(`/payments/verify/${encodeURIComponent(reference)}`)
      .then(async ({ data }) => {
        if (data.success) {
          setResult(data);
          setStatus('success');
          // FIX: Update the payment gate in AuthContext immediately
          await refreshPaymentStatus();
        } else {
          setResult(data);
          setStatus('failed');
        }
      })
      .catch(err => {
        setResult({ message: err.response?.data?.message || err.message || 'Verification failed.' });
        setStatus('failed');
      });
  }, [reference]);

  // Auto-redirect on success
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setInterval(() => {
      setCount(p => {
        if (p <= 1) {
          clearInterval(timer);
          navigate('/student/choose-cohort');
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F5F5' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div style={{ height: 4, background: '#DD0000' }} />

          <div className="p-8 text-center">

            {/* Loading */}
            {status === 'loading' && (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background: '#f5f5f5' }}>
                  <svg className="animate-spin w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#DD0000" strokeWidth="3"
                      strokeDasharray="50 10" strokeLinecap="round" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying your payment…</h2>
                <p className="text-gray-500 text-sm">Please wait while we confirm with Paystack.</p>
                {reference && (
                  <p className="text-xs text-gray-400 mt-3 font-mono break-all">
                    Ref: {reference}
                  </p>
                )}
              </>
            )}

            {/* Success */}
            {status === 'success' && (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background: '#f0fdf4' }}>
                  <span className="text-4xl">🎉</span>
                </div>
                <h2 className="text-2xl font-bold mb-1" style={{ color: '#16a34a' }}>
                  Payment Successful!
                </h2>
                <p className="text-gray-500 text-sm mb-5">
                  {result?.studentName
                    ? `Welcome, ${result.studentName}!`
                    : 'Your level has been unlocked.'}
                </p>

                <div className="bg-gray-50 rounded-xl p-4 mb-5 text-left space-y-2">
                  {result?.level && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Level Unlocked</span>
                      <span className="font-bold" style={{ color: '#DD0000' }}>
                        {result.level}
                      </span>
                    </div>
                  )}
                  {result?.amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Amount Paid</span>
                      <span className="font-bold text-gray-900">
                        GHS {Number(result.amount).toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {reference && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Reference</span>
                      <span className="font-mono text-xs text-gray-600 truncate max-w-[180px]">
                        {reference}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => navigate('/student/choose-cohort')}
                  className="w-full py-3.5 rounded-xl font-bold text-sm text-white mb-3"
                  style={{ background: '#DD0000' }}>
                  Choose Your Class Time →
                </button>

                <p className="text-xs text-gray-400">
                  Redirecting in <span className="font-bold text-gray-700">{countdown}s</span>…
                </p>
              </>
            )}

            {/* Failed */}
            {status === 'failed' && (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background: '#fff2f2' }}>
                  <span className="text-4xl">❌</span>
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#DD0000' }}>
                  Payment Failed
                </h2>
                <p className="text-gray-500 text-sm mb-5">
                  {result?.message || 'Your payment could not be processed. No money has been deducted.'}
                </p>

                {reference && (
                  <div className="bg-gray-50 rounded-xl p-3 mb-5">
                    <p className="text-xs text-gray-500 mb-1">Reference</p>
                    <p className="text-xs font-mono text-gray-700 break-all">{reference}</p>
                  </div>
                )}

                <button
                  onClick={() => navigate('/register')}
                  className="w-full py-3.5 rounded-xl font-bold text-sm text-white mb-3"
                  style={{ background: '#DD0000' }}>
                  Try Again
                </button>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-1">Need help?</p>
                  <a href="tel:0202071417"
                    className="text-sm font-bold" style={{ color: '#DD0000' }}>
                    📞 0202071417
                  </a>
                  <p className="text-xs text-gray-400 mt-1">
                    Quote your reference number when calling.
                  </p>
                </div>
              </>
            )}

          </div>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          🔒 Payments secured by Paystack · DeutschBridge Institute
        </p>
      </div>
    </div>
  );
}
